/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useSelector } from "react-redux";
import { initialGeoRegion } from "../../../../components/helpers";
import { useCreateRegionMutation, useDeleteRegionMutation, useGetGeoRegionsQuery, useUpdateRegionMutation } from "../../../../store/api/admin/adminApiSlice";
import { IGeoRegion } from "../../../../store/api/admin/types";
import { setBasicDialogOpenFor, setConfirmDialog, setErrorMsg, setOpenToast } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { useEffect, useState } from "react";
import { errorMessage, } from "../../../../utils/helpers";
import { API, DIALOGOPENFOR } from "../../../../globals/globals";
import { basicDialogSelector, confirmDialogSelector } from "../../../../store/helpers/helperSelectors";

interface IPayload {
  [key: string]: string;
}

export const useAddEditRegion = () => {
  const dispatch = useStoreDispatch();

  const [updateRegion] = useUpdateRegionMutation();
  const [createRegion] = useCreateRegionMutation();
  const [deleteRegion] = useDeleteRegionMutation();

  const openModal = useSelector(basicDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const { geoRegions, refetchGeoRegions } = useRegionDisplay();

  const [selectedRegion, setSelectedRegion] = useState<IGeoRegion>(initialGeoRegion);
  const [permittedGeoRegions, setPermittedGeoRegions] = useState<IGeoRegion[] | null>(null);
  const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);

  const [errMsg, setErrMsg] = useState<IPayload>({});

  // Force a refetch first thing
  useEffect(() => {
    // console.log("Fetching regions");
    refetchGeoRegions();
  }, [refetchGeoRegions]);

  // This will run once when the Region page first loads
  useEffect(() => {
    if (geoRegions && geoRegions.length) {
      // console.log("Setting permitted regions");
      let regions = geoRegions.filter((item, index) => index != 0).sort((a: IGeoRegion, b: IGeoRegion) => a.name.localeCompare(b.name));
      let initialGeoRegion = geoRegions[0];
      setPermittedGeoRegions([initialGeoRegion, ...regions]);
      setSelectedRegion(geoRegions[0]);
    }
  }, [geoRegions]);

  // This will run whenever the user clicks a Region (or the "Add" button)
  useEffect(() => {
    // console.log("Editing region selection changed: " + selectedRegion.name);
    setErrMsg({});
    dispatch(setErrorMsg({}));
    setSaveButtonEnabled(false);

    if (selectedRegion.objectKey.length && permittedGeoRegions?.length) {
      // Ensure the region this user selected is included in their permitted regions
      const regions = permittedGeoRegions?.filter(region => region.objectKey === selectedRegion.objectKey);
      const originalRegion = regions.length > 0 ? regions[0] : initialGeoRegion;
      setSaveButtonEnabled(originalRegion.name !== selectedRegion.name);
    } else {
      setSaveButtonEnabled(selectedRegion.name.length > 0);
    }
  }, [selectedRegion, permittedGeoRegions]);

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;
    if (name === "name") {
      if (value?.length === 0) {
        setErrMsg({ ...errMsg, [name]: `Region name cannot be blank` });
      }
      else {
        setErrMsg({ ...errMsg, [name]: `` });
      }
    }
  };

  /*
  const handleDefaultChange = (event: any) => {
    const updatedRegion: IGeoRegion = { ...selectedRegion, isDefault: event.target.checked };
    setSelectedRegion(updatedRegion);
  };
  */

  const handleNameChange = (event: any) => {
    const updatedRegion: IGeoRegion = { ...selectedRegion, name: event.target.value };
    setSelectedRegion(updatedRegion);
  }

  const deleteClickHandler = () => {
    const regionsToDelete: IGeoRegion | undefined = permittedGeoRegions?.find(region => region.objectKey === selectedRegion.objectKey);
    if (regionsToDelete && regionsToDelete.objectKey.length) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.REGION));
    }
  };

 
 

  const deleteRegionByItem = async (item: any) => {
    if (!item) return;
    const region = permittedGeoRegions?.find((r: IGeoRegion) => r.objectKey === item.value);
    if (!region) return;
    try {
      const result: any = await deleteRegion(region.objectKey);
      if (result.error || !result.data) {
        throw result.error?.data || "Unknown error";
      }
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `${region.name} has been deleted`,
        })
      );
      setSelectedRegion(initialGeoRegion);
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
    refetchGeoRegions();
  };

  const validateRegion = (): IGeoRegion | null => {
    if (!selectedRegion) return null;

    if (selectedRegion.name.length === 0) {
      setErrMsg({ name: "Name cannot be blank" });
      return null;
    }

    const updatedRegion = { ...selectedRegion };
    return updatedRegion;
  }

  const handleSave = async (handleCancel: () => void) => {
    if (!selectedRegion) {
      return;
    }

    const updatedRegion = validateRegion();
    if (updatedRegion) {
      return await submitHandler(updatedRegion, handleCancel);
    }
  };

  const submitHandler = async (geoRegion: IGeoRegion, handleCancel: () => void) => {
    if (!geoRegion || !geoRegion.name.length) return;

    try {
      const result: any = await (
        geoRegion.objectKey.length
          ? updateRegion(geoRegion)
          : createRegion(geoRegion));

      if (result.error || !result.data) {
        throw result.error.data || "Unknown error";
      }

      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `Region ${geoRegion.objectKey.length === 0 ? "created" : "updated"}: ${geoRegion.name || "unknown"}`,
        }));

      // If we were creating a new Region, this will close the modal
      if (geoRegion.objectKey.length === 0) {
        handleCancel();
      }

    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error?.messages?.name || "Invalid region name!!"),
        })
      );
    }

    refetchGeoRegions();
  };

  return {
    initialGeoRegion,
    permittedGeoRegions,
    selectedRegion,
    setSelectedRegion,
    saveButtonEnabled, setSaveButtonEnabled,
    openModal,
    openConfirmDialog,
    errMsg,
    onBlurHandler,
    deleteClickHandler,
    deleteRegionByItem,
    handleNameChange,
    handleSave,
    refetchGeoRegions,
    geoRegions
  };
}

export const useRegionDisplay = () => {
  const { data = [], isLoading, refetch } = useGetGeoRegionsQuery(
    undefined,
    { pollingInterval: API.ENDPOINTS.SITEGROUPS.pollingInterval });

  return {
    geoRegions: data,
    loadingGeoRegions: isLoading,
    refetchGeoRegions: refetch,
  };
};
